// DS2 — 시안 부품(base · common · hub_extra 값 그대로). 서버 컴포넌트, props 만(데이터 페치 금지 · 숫자는 호출부 DB 실값).
//   DdayPill(.dd) · DateCard/SchedGrid(.sched .sc) · StageBar(.steps) · RedevRow/RedevList(.two .rd) · RegionTile/RegionGrid(.reg .rg)
//   PostCard/PostGrid(.posts .post) · UnderlineTabs(.tabs) · PageTitle(.pt) · Crumb(.crumb) · Chip(.chip) · Faq(.faq) · RatioBar
import type { ReactNode } from 'react';
import { clean, kstDateParts, MetaSpans, type MetaItem } from './meta';
import './ds2.css';

/* ── D-day pill (.dd) — navy 채움 11/700 · D-1 이하(오늘 포함) #C2410C(.hot) · 지난 일정 past(흰 바탕·회색) ── */
export function DdayPill({ days, label, past }: { days?: number | null; label?: string | null; past?: boolean }) {
  let text: string | null = clean(label) ? String(label) : null;
  if (!text && typeof days === 'number' && Number.isFinite(days)) text = days === 0 ? 'D-day' : days > 0 ? `D-${days}` : null;
  if (!text) return null;
  const attrs: Record<string, string> = {};
  if (past) attrs['data-past'] = '';
  else if (typeof days === 'number' && days >= 0 && days <= 1) attrs['data-hot'] = '';
  return <span className="ds2-dd" {...attrs}>{text}</span>;
}

/* ── 이번 주 청약 날짜 카드 (.sc) ── */
export interface DateCardProps {
  title: string;
  href?: string | null;
  /** 날짜 블록의 날짜('YYYY-MM-DD' KST 달력 또는 Date). 큰 숫자 = 일, 아래 = 「10월 토」(요일은 날짜에서 계산). */
  date: string | Date;
  /** 메타(「부산 연제구 · 499세대」) — 항목마다 span. */
  meta?: MetaItem[];
  /** 접수 기간 한 줄(「10/19~10/21」, 12 sub tabular). */
  range?: string | null;
  days?: number | null;
  ddLabel?: string | null;
  /** 지난 일정(제목 sub · pill 흰 바탕). */
  past?: boolean;
  as?: 'h3' | 'h4';
  /** ds4 — 카드 루트 data-*(예: sortKeyAttrs(키) → data-ds4-sort-key). */
  dataAttrs?: Record<`data-${string}`, string>;
  /** thumb-lists — 날짜 블록 앞 48px 썸네일(components/thumb/Thumb). 없으면 종전 마크업 그대로. */
  thumb?: ReactNode;
}

export function DateCard({ title, href, date, meta, range, days, ddLabel, past, as = 'h3', dataAttrs, thumb }: DateCardProps) {
  const T = as;
  const p = kstDateParts(date);
  if (!p) return null;
  const m = MetaSpans({ items: meta });
  return (
    <article className="ds2-sc" data-ds2-card="schedule" {...(dataAttrs ?? {})} {...(past ? { 'data-past': '' } : {})}>
      {thumb ? <div className="ds2-sc__th">{thumb}</div> : null}
      <div className="ds2-sc__date">
        <b>{p.d}</b>
        <span>{`${p.m}월 (${p.wd})`}</span>{/* hc5-int(live1-d sweep V) — 「10월 월」 요일 연접 → 「10월 (월)」 */}
      </div>
      <div className="ds2-sc__bd">
        <T>{href ? <a href={href}>{title}</a> : title}</T>
        {m ? <div className="ds2-sc__m">{m}</div> : null}
        {clean(range) ? <span className="ds2-sc__rng">{range}</span> : null}
        <DdayPill days={days} label={ddLabel} past={past} />
      </div>
    </article>
  );
}

/** region_home1b — 캐러셀 슬라이드 속성(aria-roledescription="slide" · 「n / 전체」 이름). 홈 카드 줄·히어로 공용. */
export function slideAttrs(i: number, n: number): { 'aria-roledescription': 'slide'; 'aria-label': string } {
  return { 'aria-roledescription': 'slide', 'aria-label': `${i + 1} / ${n}` };
}

/** 날짜 카드 4열(≤760 가로 스크롤 78%). */
export function SchedGrid({ children, ariaLabel, dataAttrs, slides }: { children: ReactNode; ariaLabel?: string; dataAttrs?: Record<`data-${string}`, string>; /** region_home1b — 캐러셀 줄 안이면 li 마다 aria-roledescription="slide"(없으면 종전 마크업). */ slides?: boolean }) {
  const items = (Array.isArray(children) ? children.flat() : [children]).filter((c) => c !== null && c !== undefined && c !== false);
  return (
    <ul className="ds2-sched" aria-label={ariaLabel} data-ds2="sched" {...(dataAttrs ?? {})}>
      {items.map((c, i) => <li key={(c as { key?: string | number | null })?.key ?? i} {...(slides ? slideAttrs(i, items.length) : {})}>{c}</li>)}
    </ul>
  );
}

/* ── 8칸 단계 막대 (.steps) — 진행칸 navy2 · 끝 단계명 12/700 navy2 ── */
export function StageBar({ step, total = 8, label, ariaLabel }: { step: number; total?: number; label?: string | null; ariaLabel?: string }) {
  const n = Math.max(1, Math.floor(total));
  const s = Math.max(0, Math.min(n, Math.floor(Number.isFinite(step) ? step : 0)));
  return (
    <div className="ds2-steps" role="img" aria-label={ariaLabel ?? `${n}단계 중 ${s}단계${clean(label) ? ` ${label}` : ''}`}>
      {Array.from({ length: n }, (_, i) => (i < s ? <i key={i} data-on="" /> : <i key={i} />))}
      {clean(label) ? <em>{label}</em> : null}
    </div>
  );
}

/* ── 재개발 행 (.rd) ── */
export interface RedevRowProps {
  name: string;
  href?: string | null;
  /** 이름 옆 작은 회색(hub: 「재건축」 등). */
  small?: string | null;
  /** 우측 「남구 · 9,092세대」. */
  meta?: MetaItem[];
  step: number;
  total?: number;
  label?: string | null;
  /** ds4 — 행 루트 data-*(예: sortKeyAttrs(키)). */
  dataAttrs?: Record<`data-${string}`, string>;
  /** thumb-lists — 행 왼쪽 48px 썸네일(두 줄 높이). 없으면 종전 마크업 그대로. */
  thumb?: ReactNode;
}

export function RedevRow({ name, href, small, meta, step, total = 8, label, dataAttrs, thumb }: RedevRowProps) {
  const m = MetaSpans({ items: meta });
  return (
    <div className="ds2-rd" data-ds2-card="redev" {...(dataAttrs ?? {})} {...(thumb ? { 'data-thumb': '' } : {})}>
      {thumb ? <div className="ds2-rd__th">{thumb}</div> : null}
      <b>{href ? <a href={href}>{name}</a> : name}{clean(small) ? <small>{small}</small> : null}</b>
      <span className="ds2-rd__u">{m}</span>
      <StageBar step={step} total={total} label={label} />
    </div>
  );
}

/** 2단(≤760 1단) — 앞 절반 왼쪽 · 뒤 절반 오른쪽(시안 열 우선 순서). ds3 추가: cols=3 → 3단(≤760 1단, 열 우선 ⌈n/3⌉씩). */
export function RedevList({ rows, cols = 2, dataAttrs, slides }: { rows: RedevRowProps[]; cols?: 2 | 3; dataAttrs?: Record<`data-${string}`, string>; /** region_home1b — 캐러셀 줄 안이면 열(div)마다 role=group + aria-roledescription="slide". */ slides?: boolean }) {
  if (!rows.length) return null;
  const n = cols === 3 ? 3 : 2;
  const h = Math.ceil(rows.length / n);
  const cnt = Math.min(n, Math.ceil(rows.length / h));
  const idx = { l: 0, m: 1, r: n === 3 ? 2 : 1 } as const;
  const col = (rs: RedevRowProps[], k: 'l' | 'm' | 'r') => (
    <div key={k} {...(slides && rs.length ? { role: 'group', ...slideAttrs(idx[k], cnt) } : {})}>{rs.map((r, i) => <RedevRow key={`${r.href ?? r.name}-${i}`} {...r} />)}</div>
  );
  if (n === 3) {
    return <div className="ds2-two" data-ds2="redev-list" data-cols="3" {...(dataAttrs ?? {})}>{col(rows.slice(0, h), 'l')}{col(rows.slice(h, 2 * h), 'm')}{col(rows.slice(2 * h), 'r')}</div>;
  }
  return <div className="ds2-two" data-ds2="redev-list" {...(dataAttrs ?? {})}>{col(rows.slice(0, h), 'l')}{col(rows.slice(h), 'r')}</div>;
}

/* ── 3px 비례 막대 ── */
export function RatioBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 && Number.isFinite(value) ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return <i aria-hidden="true"><em style={{ width: `${Math.round(pct)}%` }} /></i>;
}

/* ── 지역 타일 (.rg) ── */
/** KWX 레인 C — suffix: 이름 뒤 보조 줄(예: 「아파트 분양·청약」) · 링크 글자에 그대로 들어간다(보이는 글자 = 링크 글자 · 12px). */
export function RegionTile({ name, suffix, count, max, href, countLabel, dataAttrs }: { name: string; suffix?: string | null; count: number; max: number; href?: string | null; countLabel?: string; dataAttrs?: Record<`data-${string}`, string> }) {
  const body = (
    <>
      <b>{name}</b>
      <span>{countLabel ?? count.toLocaleString('ko-KR')}</span>
      {/* ui-1101 — 좁은 타일(4열)에서 「아파트 분양 / ·청약」 처럼 가운뎃점 앞에서 끊겼다 → 가운뎃점 양옆은 붙이고(단어 잇기) 띄어쓰기에서만 줄바꿈. */}
      {clean(suffix) ? <>{' '}<small className="ds2-rg__sfx">{String(suffix).replace(/·/g, '\u2060·\u2060')}</small></> : null}
      <RatioBar value={count} max={max} />
    </>
  );
  const da = dataAttrs ?? {};
  return href ? <a className="ds2-rg" href={href} data-ds2-card="region" {...da}>{body}</a> : <div className="ds2-rg" data-ds2-card="region" {...da}>{body}</div>;
}

/** 6열(≤760 4열 · 숫자는 이름 아래) · cols=8(허브 시군구). max 는 items 의 최대값(=100%). */
export function RegionGrid({ items, cols = 6, ariaLabel, dataAttrs }: { items: Array<{ name: string; suffix?: string | null; count: number; href?: string | null; countLabel?: string; dataAttrs?: Record<`data-${string}`, string> }>; cols?: 6 | 8; ariaLabel?: string; dataAttrs?: Record<`data-${string}`, string> }) {
  const list = items.filter((x) => clean(x.name) && Number.isFinite(x.count));
  if (!list.length) return null;
  const max = Math.max(...list.map((x) => x.count));
  return (
    <ul className="ds2-reg" aria-label={ariaLabel} data-ds2="region-grid" {...(dataAttrs ?? {})} {...(cols === 8 ? { 'data-cols': '8' } : {})}>
      {list.map((x) => <li key={x.href ?? x.name}><RegionTile {...x} max={max} /></li>)}
    </ul>
  );
}

/* ── 분석 글 가로형 (.post) ── */
export interface PostCardProps {
  title: string;
  href?: string | null;
  thumbSrc?: string | null;
  thumbAlt?: string | null;
  /** 「10.16 · 해운대구」. */
  meta?: MetaItem[];
  thumb?: ReactNode;
  as?: 'h3' | 'h4';
  /** ds4 — 카드 루트 data-*(예: sortKeyAttrs(키)). */
  dataAttrs?: Record<`data-${string}`, string>;
}

export function PostCard({ title, href, thumbSrc, thumbAlt, meta, thumb, as = 'h3', dataAttrs }: PostCardProps) {
  const T = as;
  const src = clean(thumbSrc) ? String(thumbSrc) : '';
  const m = MetaSpans({ items: meta });
  return (
    <article className="ds2-post" data-ds2-card="post" {...(dataAttrs ?? {})}>
      <div className="ds2-post__th">
        {thumb ?? (src ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={src} alt={clean(thumbAlt) ? String(thumbAlt) : ''} width={64} height={48} loading="lazy" decoding="async" />
        ) : null)}
      </div>
      <div>
        <T>{href ? <a href={href}>{title}</a> : title}</T>
        {m ? <span>{m}</span> : null}
      </div>
    </article>
  );
}

/** 3열(≤760 1열). */
export function PostGrid({ children, ariaLabel, dataAttrs, slides }: { children: ReactNode; ariaLabel?: string; dataAttrs?: Record<`data-${string}`, string>; /** region_home1b — SchedGrid slides 와 같음. */ slides?: boolean }) {
  const items = (Array.isArray(children) ? children.flat() : [children]).filter((c) => c !== null && c !== undefined && c !== false);
  return (
    <ul className="ds2-posts" aria-label={ariaLabel} data-ds2="post-grid" {...(dataAttrs ?? {})}>
      {items.map((c, i) => <li key={(c as { key?: string | number | null })?.key ?? i} {...(slides ? slideAttrs(i, items.length) : {})}>{c}</li>)}
    </ul>
  );
}

/* ── 밑줄 탭 (.tabs) — current 는 aria-current="page" ── */
export function UnderlineTabs({ items, sticky, ariaLabel }: { items: Array<{ label: string; href: string; count?: number | string | null; current?: boolean }>; sticky?: boolean; ariaLabel?: string }) {
  const list = items.filter((x) => clean(x.label) && clean(x.href));
  if (!list.length) return null;
  return (
    <nav className="ds2-tabs" aria-label={ariaLabel} {...(sticky ? { 'data-sticky': '' } : {})}>
      {list.map((x) => (
        <a key={x.href} href={x.href} {...(x.current ? { 'aria-current': 'page' as const } : {})}>
          {x.label}
          {clean(x.count) ? <b>{typeof x.count === 'number' ? x.count.toLocaleString('ko-KR') : x.count}</b> : null}
        </a>
      ))}
    </nav>
  );
}

/* ── 페이지 제목 (.pt) · 빵부스러기 (.crumb) ── */
export function PageTitle({ title, sub, titleAs = 'h1', titleClassName }: { title: ReactNode; sub?: ReactNode; titleAs?: 'h1' | 'h2'; titleClassName?: string }) {
  const H = titleAs;
  return (
    <div className="ds2-pt" data-ds2="page-title">
      <H className={titleClassName}>{title}</H>
      {sub ? <p>{sub}</p> : null}
    </div>
  );
}

export function Crumb({ items }: { items: Array<{ label: string; href?: string | null }> }) {
  const list = items.filter((x) => clean(x.label));
  if (!list.length) return null;
  return (
    <nav className="ds2-crumb" aria-label="현재 위치">
      {list.map((x, i) => (
        <span key={`${x.label}-${i}`}>
          {i > 0 ? <i aria-hidden="true">›</i> : null}
          {i === list.length - 1 ? <b>{x.label}</b> : x.href ? <a href={x.href}>{x.label}</a> : x.label}
        </span>
      ))}
    </nav>
  );
}

/* ── 칩 (.chip) ── */
export function Chip({ children }: { children: ReactNode }) {
  return <span className="ds2-chip">{children}</span>;
}

/* ── FAQ (.faq) — details/summary ── */
export function Faq({ items }: { items: Array<{ q: string; a: ReactNode }> }) {
  const list = items.filter((x) => clean(x.q));
  if (!list.length) return null;
  return (
    <div className="ds2-faq" data-ds2="faq">
      {list.map((x, i) => (
        <details key={i}>
          <summary>{x.q}</summary>
          <p>{x.a}</p>
        </details>
      ))}
    </div>
  );
}
